import React, { useEffect, useState, useContext } from 'react';
import { UserContext } from '../contexts/UserContext';
import axiosInstance from '../config/axiosConfig';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Button
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import BadgeIcon from '@mui/icons-material/Badge';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AppsIcon from '@mui/icons-material/Apps';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';

export default function MyUser() {
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useContext(UserContext);

  const fetchUsuario = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Consume el nuevo endpoint unificado /api/User/{id}
      const response = await axiosInstance.get(`/User/${user.id}`);
      setUsuario(response.data);
      setError(null);
    } catch (err) {
      console.error('Error al obtener usuario:', err);
      setError('No se pudo cargar la información del usuario.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsuario();
  }, [user?.id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
        <CircularProgress size={45} />
        <Typography variant="body1" color="text.secondary">
          Cargando datos del usuario...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="error" action={
          <Button color="inherit" size="small" onClick={fetchUsuario}>Reintentar</Button>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!usuario) {
    return (
      <Box sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
        <Alert severity="warning">No se encontró información para este usuario.</Alert>
      </Box>
    );
  }

  const isEnabled = usuario.enable === 1;

  return (
    <Box sx={{ width: '100%', maxWidth: 1050, mx: 'auto', p: { xs: 2, md: 4 } }}>
      {/* TARJETA CABECERA DE PERFIL */}
      <Card elevation={2} sx={{ borderRadius: 3, mb: 3, overflow: 'hidden' }}>
        <Box sx={{ height: 110, background: 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)' }} />
        
        <CardContent sx={{ pt: 0, pb: 3, position: 'relative' }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'center', sm: 'flex-end' }, mt: -7, mb: 2, gap: 2.5 }}>
            <Avatar
              sx={{
                width: 110,
                height: 110,
                bgcolor: '#0d47a1',
                border: '4px solid #fff',
                boxShadow: 2,
                fontSize: 40,
                fontWeight: 700
              }}
            >
              {usuario.firstName ? usuario.firstName.charAt(0).toUpperCase() : <PersonIcon sx={{ fontSize: 60 }} />}
            </Avatar>

            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#2c3e50' }}>
                {usuario.fullName || usuario.username}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                @{usuario.username}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Chip
                icon={<BadgeIcon />}
                label={usuario.roleName || 'Sin Rol'}
                color="primary"
                variant="outlined"
                sx={{ fontWeight: 600 }}
              />
              <Chip
                icon={isEnabled ? <CheckCircleIcon /> : <CancelIcon />}
                label={usuario.statusName || (isEnabled ? 'Habilitado' : 'Deshabilitado')}
                color={isEnabled ? 'success' : 'default'}
                sx={{ fontWeight: 600 }}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* COLUMNA IZQUIERDA: DATOS DE CONTACTO Y ACCESO */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon fontSize="small" /> Información Personal y Contacto
            </Typography>
            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <EmailIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Correo Electrónico</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{usuario.email || 'No registrado'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <PhoneIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Teléfono de Contacto</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{usuario.phone || 'No registrado'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <CalendarMonthIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Fecha de Registro en el Sistema</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{usuario.registrationDate || 'No disponible'}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* COLUMNA DERECHA: PUESTO E INSTITUCIÓN */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon fontSize="small" /> Cargo e Institución
            </Typography>
            <Divider sx={{ mb: 2.5 }} />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <BusinessIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Institución</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{usuario.institutionName || 'No asignada'}</Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <WorkIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>Puesto de Trabajo</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{usuario.jobName || 'No asignado'}</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* SECCIÓN INFERIOR 1: APLICACIONES AUTORIZADAS */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AppsIcon fontSize="small" /> Aplicaciones Autorizadas
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {usuario.applicationNames && usuario.applicationNames.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {usuario.applicationNames.map((app, idx) => (
                  <Chip
                    key={idx}
                    label={app}
                    size="small"
                    sx={{ backgroundColor: '#e3f2fd', color: '#0d47a1', fontWeight: 600 }}
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Sin aplicaciones asignadas.
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* SECCIÓN INFERIOR 2: COBERTURA GEOGRÁFICA */}
        <Grid item xs={12} md={6}>
          <Paper elevation={1} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1976d2', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocationOnIcon fontSize="small" /> Cobertura Geográfica
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Roles Nacionales (Admin, Consulta Nacional, etc.) */}
            {[1, 2, 7].includes(Number(usuario.idRol)) ? (
              <Box sx={{ p: 1.5, bgcolor: '#e8f5e9', borderRadius: 2, border: '1px solid #c8e6c9' }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                  Cobertura Nacional Completa (22 Departamentos)
                </Typography>
              </Box>
            ) : usuario.departmentNames && usuario.departmentNames.length > 0 ? (
              /* Roles Departamentales / Interdepartamentales */
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 1 }}>
                  Departamentos Asignados:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, maxHeight: 150, overflowY: 'auto' }}>
                  {usuario.departmentNames.map((dep, idx) => (
                    <Chip key={idx} label={dep} size="small" color="primary" variant="outlined" />
                  ))}
                </Box>
              </Box>
            ) : usuario.assignedMunicipalities && usuario.assignedMunicipalities.length > 0 ? (
              /* Roles Municipales / Intermunicipales agrupados por Departamento */
              <Box sx={{ maxHeight: 220, overflowY: 'auto', pr: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {Object.entries(
                  usuario.assignedMunicipalities.reduce((acc, curr) => {
                    if (!acc[curr.departmentName]) acc[curr.departmentName] = [];
                    acc[curr.departmentName].push(curr.municipalityName);
                    return acc;
                  }, {})
                ).map(([depto, munis]) => (
                  <Box 
                    key={depto} 
                    sx={{ 
                      bgcolor: '#f8f9fa', 
                      p: 1.5, 
                      borderRadius: 2, 
                      border: '1px solid #e2e8f0' 
                    }}
                  >
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#1565c0', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                      📍 {depto}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                      {munis.map((muni, idx) => (
                        <Chip 
                          key={idx} 
                          label={muni} 
                          size="small" 
                          sx={{ bgcolor: '#ffffff', border: '1px solid #cbd5e1', fontWeight: 500 }} 
                        />
                      ))}
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Sin cobertura geográfica asignada.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchUsuario}
          size="small"
        >
          Actualizar Perfil
        </Button>
      </Box>
    </Box>
  );
}